import React, { useEffect } from 'react';
import styles from './styles.module.scss';

// manifests

// assets

// services

// props
import { IPaginationResourceShort } from '../../../../../props/general';

// components
import { ActionButton, DatePicker, DetailsList, Dropdown, IColumn, IconButton, SelectionMode, SpinnerSize, Stack, TextField } from '@fluentui/react';
import FormPagination, { IFormPage } from '../../../../uiframeworks/forms/pagination';
import { IExtractedVesselRequisitionFormProps, IVesselRequisitionResourceProps } from '../../../../../props/vessels/requisitions/requisition';
import GeneralService from '../../../../../services/general';
import moment from 'moment';
import { useStore } from '../../../../../stores/root';
import { IQuantityUnitResourceShortProps } from '../../../../../props/general/quantityUnit';
import QuantityUnitService from '../../../../../services/general/quantityUnit';
import LoadingComponent from '../../../../feedbacks/loading';
import FormFooter from '../../../../uiframeworks/forms/footer';
import SelectVessel from '../../../../uiframeworks/forms/vessels';
import ValidationService from '../../../../../services/validation';
import Text from '../../../../typography/text';
import { IVesselRequisitionItemResourceProps, IVesselRequisitionItemResourceShortProps } from '../../../../../props/vessels/requisitions/item';
import VesselRequisitionService from '../../../../../services/vessels/requisitions/requisition';
import { faCheck } from '@fortawesome/pro-light-svg-icons';
import LoadingDialogComponent from '../../../../feedbacks/loadingDialog';
import VesselRequisitionItemSubform from './subforms/item';
import Label from '../../../../typography/label';
import FileService from '../../../../../services/general/file';

type VesselRequisitionFormProps = {
    requisitionId?: string;
}

const VesselRequisitionForm: React.FC<VesselRequisitionFormProps> = (props: VesselRequisitionFormProps) => {
    const { user, route } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const [submitted, setSubmitted] = React.useState<boolean>(false);
    const [data, setData] = React.useState<Partial<IVesselRequisitionResourceProps>>({
        id: GeneralService.guid(),
        createdAt: moment().toISOString(),
        createdBy: user?.data || undefined
    });
    const [error, setError] = React.useState<any>({});
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();
    const [activeItem, setActiveItem] = React.useState<IVesselRequisitionItemResourceProps | undefined>();
    const [quantityUnits, setQuantityUnits] = React.useState<IQuantityUnitResourceShortProps[]>([]);

    const [physicalForm, setPhysicalForm] = React.useState<File | undefined>();
    const [preview, setPreview] = React.useState<string[] | undefined>();
    const [loadingPreview, setLoadingPreview] = React.useState<boolean>(false);
    const [extracting, setExtracting] = React.useState<boolean>(false);

    const mode = props.requisitionId ? 'update' : 'create';

    const columns: IColumn[] = [
        {
            key: 'name',
            name: 'Name',
            minWidth: 100,
            onRender: (item: IVesselRequisitionItemResourceShortProps, idx) => {
                return <Stack styles={{root: {padding: '6px 0px'}}}>
                    <Label size={'xsmall'}>{item.goods.name}</Label>
                    {item.notes ? <Text size={'xsmall'} style={{fontStyle: 'italic'}}>Notes: {item.notes}</Text> : null}
                </Stack>
            }
        },
        {
            key: 'purpose',
            name: 'Notes / Purpose',
            minWidth: 300,
            maxWidth: 300,
            onRender: (item: IVesselRequisitionItemResourceShortProps, idx) => {
                return <Stack styles={{root: {padding: '6px 0px'}}}>
                    <Text size={'small'}>{item.purpose || "-"}</Text>
                </Stack>
            }
        },
        {
            key: 'size',
            name: 'Size (length x width x height)',
            minWidth: 200,
            maxWidth: 200,
            onRender: (item: IVesselRequisitionItemResourceShortProps, idx) => {
                return <Stack styles={{root: {padding: '6px 0px'}}} horizontal tokens={{ childrenGap: 3 }}>
                    <Text size={"small"}>{GeneralService.getNumberWithSeparator(Number(item.goods?.length || "0").toFixed(2))} cm</Text>
                    <Text size={'small'}>x</Text>
                    <Text size={"small"}>{GeneralService.getNumberWithSeparator(Number(item.goods?.width || "0").toFixed(2))} cm</Text>
                    <Text size={'small'}>x</Text>
                    <Text size={"small"}>{GeneralService.getNumberWithSeparator(Number(item.goods?.height || "0").toFixed(2))} cm</Text>
                </Stack>
            }
        },
        {
            key: 'quantity',
            name: 'Quantity',
            minWidth: 100,
            maxWidth: 100,
            onRender: (item: IVesselRequisitionItemResourceShortProps, idx) => {
                return <Stack styles={{root: {padding: '6px 0px'}}}>
                    {item.quantityAmount ? <Text size={'small'}>{GeneralService.getNumberWithSeparator(Number(item.quantityAmount))} {item.quantityUnit?.name || ""}</Text> : null}
                </Stack>
            }
        },
        {
            key: 'weight',
            name: 'Weight',
            minWidth: 100,
            maxWidth: 100,
            onRender: (item: IVesselRequisitionItemResourceShortProps, idx) => {
                return <Stack styles={{root: {padding: '6px 0px'}}}>
                    {item.goods?.weightAmount ? <Text size={'small'}>{GeneralService.getNumberWithSeparator(Number(item.goods?.weightAmount))} {item.goods?.weightUnit || ""}</Text> : <Text size={'small'}>-</Text>}
                </Stack>
            }
        },
        {
            key: 'actions',
            name: '',
            minWidth: 74,
            maxWidth: 74,
            onRender: (item: IVesselRequisitionItemResourceShortProps) => {
                return <Stack horizontal tokens={{ childrenGap: 10 }}>
                    <IconButton iconProps={{ iconName: "Edit" }} onClick={() => {
                        setActiveItem(item);
                        setActiveSurface('items.update');
                    }} />
                    <IconButton iconProps={{ iconName: "Delete" }} onClick={() => {
                        const _data = data;
                        _data.items = (_data.items || []).filter((i) => i.id !== item.id);

                        setData({..._data});
                    }} />
                </Stack>
            }
        }
    ];

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        const _quantityUnits = await QuantityUnitService.retrieve();
        setQuantityUnits(_quantityUnits);

        setLoaded(true);
    }

    const _onSubmit = async () => {
        try {
            setSubmitting(true);
            const { name, vessel, notes, items } = data;

            const fd = new FormData();
            fd.append('name', name || "");
            fd.append('vesselId', vessel?.id || "");
            fd.append('notes', notes || "");

            (items || []).forEach((item) => {
                fd.append('items[]', JSON.stringify({
                    ...item,
                    quantityUnitId: item.quantityUnit.id
                }));
            });

            let requisition = undefined;
            if (props.requisitionId) {
                requisition = await VesselRequisitionService.update(props.requisitionId, fd);
            } else {
                requisition = await VesselRequisitionService.create(fd);
            }
            setSubmitted(true);
            route.route?.history.push("/vessels/requisitions/" + requisition.id);
        } catch (e) {
            setSubmitting(false);
        }
    }

    const _onItemSurfaceDismissed = (item?: IVesselRequisitionItemResourceProps) => {
        if (item) {
            let _data = data;
            _data.items = _data.items || [];

            const iidx = (_data.items || []).findIndex((i) => i.id === item.id);
            if (iidx > -1) {
                _data.items[iidx] = item;
            } else {
                _data.items.push(item);
            }

            setData({ ..._data });
        }

        setActiveSurface(undefined);
        setActiveItem(undefined);
    }

    const _onFileChange = async (_files: FileList | File[]) => {
        if (physicalForm && !window.confirm("Are you sure you want to replace current uploaded document?")) {
            return;
        }

        const _physicalForm = _files[0];
        setPhysicalForm(_physicalForm);

        if (_physicalForm) {
            setExtracting(true);
            const result = await extract(_physicalForm);
            if (result) {
                setData({
                    ...data,
                    vessel: result.vessel || data.vessel,
                    items: [
                        ...(data.items || []),
                        ...((result.items || []).map((item): IVesselRequisitionItemResourceShortProps => {
                            return {
                                id: GeneralService.guid(),
                                goods: item.goods || {
                                    id: GeneralService.makeid(20, false, 'new_'),
                                    name: item.actualGoodsName || "",
                                    width: ((item.width || "") + "").trim() === "" ? "0" : item.width,
                                    length: ((item.length || "") + "").trim() === "" ? "0" : item.length,
                                    height: ((item.height || "") + "").trim() === "" ? "0" : item.height,
                                    weightAmount: "",
                                    weightUnit: ""
                                },
                                purpose: item.purpose || "",
                                quantityAmount: item.quantityAmount || "",
                                quantityUnit: item.quantityUnit || quantityUnits.find((qu) => qu.name === 'UNIT') || quantityUnits[0],
                                approvalStatus: 'pending',
                                paymentStatus: 'pending',
                                deliveryStatus: 'pending',
                                pricingStatus: 'pending'
                            }
                        }))
                    ]
                })
            }

            setExtracting(false);
            _onPreviewFile(_physicalForm);
        }
    }

    const _onPreviewFile = async (file: File) => {
        setPreview(undefined);
        setLoadingPreview(true);

        const results = await FileService.getBase64(file);

        setLoadingPreview(false);
        setPreview(results);
    }

    const extract = async (file: File): Promise<IExtractedVesselRequisitionFormProps | undefined> => {
        try {
            const images = await FileService.getBase64(file);

            const fd = new FormData();
            images.forEach((img) => fd.append("base64[]", img));

            const result = await VesselRequisitionService.extract(fd);
            return result;
        } catch (e) {
            return;
        }
    }

    return <Stack className={styles.container}>
        {!loaded ? <LoadingComponent label={'Preparing form ...'} labelPosition={'right'} spinnerPosition={'start'} /> : null}
        {loaded ? <>
            <Stack tokens={{ childrenGap: 20 }}>
                <Stack grow={1} horizontal tokens={{childrenGap: 20}} styles={{root: {alignItems: 'start'}}}>
                    <Stack tokens={{childrenGap: 10}} className={styles.card} styles={{root: {width: '40%', maxWidth: 650}}}>
                        <Label size={'small'}>Upload physical form</Label>
                        <Stack className={'divider'} />
                        <input disabled={loadingPreview} type={'file'} onChange={(evt) => _onFileChange(evt?.target.files || [])} value={[]} style={{marginTop: 20}} />
                        {loadingPreview ? <LoadingComponent label={'Generating file preview ...'} labelPosition={'right'} spinnerPosition={'baseline'} size={SpinnerSize.medium} /> : null}
                        {preview && !loadingPreview ? preview.map((preview) => {
                            return <img src={preview} className={styles.image} />;
                        }) : null}
                    </Stack>
                    <Stack className={styles.card} grow={1} tokens={{ childrenGap: 20 }}>
                        <Stack horizontal tokens={{ childrenGap: 20 }}>
                            <Stack styles={{ root: { width: '25%' } }}>
                                <TextField label={'Name'}
                                    required={true}
                                    value={data.name}
                                    errorMessage={error.name}
                                    onChange={(ev, value) => {
                                        let _data = data;
                                        _data.name = value || "";

                                        const validation = ValidationService.combination(_data.name, ['required'], {});
                                        error.name = validation.message;

                                        setData({ ...data });
                                        setError({ ...error });
                                    }} />
                            </Stack>
                            <Stack styles={{ root: { width: '25%' } }}>
                                <SelectVessel label={'Vessel'}
                                    required={true}
                                    selected={data.vessel}
                                    onChange={(vessel) => {
                                        let _data = data;
                                        _data.vessel = vessel;

                                        const validation = ValidationService.combination(_data.vessel, ['required'], {});
                                        error.vessel = validation.message;

                                        setData({ ...data });
                                        setError({ ...error });
                                    }} />
                            </Stack>
                            <Stack styles={{ root: { width: '25%' } }}>
                                <DatePicker label={"Requested at"}
                                    disabled={true}
                                    formatDate={GeneralService.formatDate}
                                    value={data.createdAt ? moment(data.createdAt).toDate() : undefined} />
                            </Stack>
                            <Stack styles={{ root: { width: '25%' } }}>
                                <TextField label={"Requested by"}
                                    disabled={true}
                                    value={data.createdBy ? data.createdBy.name : undefined} />
                            </Stack>
                        </Stack>
                        <Stack className={'divider'} />
                        <TextField label={'Notes / Description'}
                            multiline
                            rows={3}
                            autoAdjustHeight
                            resizable={false}
                            onChange={(ev, value) => {
                                let _data = data;
                                _data.notes = value || "";

                                const validation = ValidationService.combination(_data.notes, ['required', 'limit'], { maxChars: 2000 });
                                error.notes = validation.message;

                                setData({ ...data });
                                setError({ ...error });
                            }} />
                        <Stack className={'divider'} />
                        <Stack tokens={{ childrenGap: 5 }}>
                            <Stack horizontal tokens={{ childrenGap: 20 }} horizontalAlign={'space-between'} verticalAlign={'center'}>
                                <Label size={'xsmall'}>Requested items</Label>
                                <ActionButton iconProps={{ iconName: "Add" }} text={'Add item'} onClick={() => {
                                    setActiveSurface('items.add');
                                }} />
                            </Stack>
                            {/*<Text style={{ fontStyle: 'italic', marginLeft: 10 }} className={'color-red'}>Note: Item with empty name will be removed when saving</Text>*/}
                            {(data.items || []).length > 0 ? <DetailsList items={data.items || []}
                                columns={columns}
                                selectionMode={SelectionMode.none}
                                compact={true} /> : null}
                            {(data.items || []).length < 1 ? <Text size={'small'} style={{fontStyle: 'italic'}}>No requested items</Text> : null}
                        </Stack>
                    </Stack>
                </Stack>
                <FormFooter position={'fixed'} variant={'card'} hasSubmitButton onSubmit={_onSubmit} />
            </Stack>
        </> : null}
        {activeSurface === 'items.add' ? <VesselRequisitionItemSubform onDismissed={_onItemSurfaceDismissed} options={{ quantityUnits }} /> : null}
        {activeSurface === 'items.update' && activeItem ? <VesselRequisitionItemSubform item={activeItem} onDismissed={_onItemSurfaceDismissed} options={{ quantityUnits }} /> : null}
        {submitting && !submitted ? <LoadingDialogComponent title={"Submitting Requisition"} secondaryText={'Please wait while we are submitting your requisition form.'} /> : null}
        {!submitting && submitted ? <LoadingDialogComponent title={'Requisition Submitted!'} secondaryText={'Your requisition form has been submitted. We will now redirect you momentarily.'} /> : null}
        {extracting ? <LoadingDialogComponent title='Extracting Form' secondaryText="Please wait while we extract requistion form details ..." /> : null}
    </Stack>;
};

export default VesselRequisitionForm;
